import {
  Box,
  Button,
  Flex,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiCalendar, FiLock } from "react-icons/fi";
import type { SourcingEventV2Dto, SourcingEventV2PatchDto } from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { ThemedDateTimePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDateTimePicker";
import { MILESTONE_NODES, type MilestoneKey } from "../utils/milestones";
import { autoKarensDays, minTenderDays } from "../utils/karens";
import { daysBetween, milestoneOffsetPatch } from "../utils/milestoneOffsets";

interface MilestoneTimelineProps {
  event: SourcingEventV2Dto;
  mode: "edit" | "summary";
  deadlineBadge?: ReactNode;
}

// Map each editable node key to the milestonePlan field it writes.
type OffsetPlanField =
  | "questionDaysBeforeBid"
  | "bidOffsetDays"
  | "evaluationOffsetDays"
  | "awardOffsetDays"
  | "contractSigningOffsetDays";

const PLAN_FIELD: Partial<Record<MilestoneKey, OffsetPlanField>> = {
  questionDeadline: "questionDaysBeforeBid",
  bidDeadline: "bidOffsetDays",
  evaluationStart: "evaluationOffsetDays",
  awardDecision: "awardOffsetDays",
  contractSigning: "contractSigningOffsetDays",
};

// Derive the displayed offset from milestoneDates — this is the sync guarantee.
const deriveOffset = (
  nodeKey: MilestoneKey,
  dates: SourcingEventV2Dto["milestoneDates"],
): number | null => {
  switch (nodeKey) {
    case "bidDeadline":
      return dates.announcement && dates.bidSubmissionDeadline
        ? daysBetween(dates.announcement, dates.bidSubmissionDeadline)
        : null;
    case "questionDeadline":
      return dates.questionDeadline && dates.bidSubmissionDeadline
        ? daysBetween(dates.questionDeadline, dates.bidSubmissionDeadline)
        : null;
    case "evaluationStart":
      return dates.bidSubmissionDeadline && dates.evaluationTarget
        ? daysBetween(dates.bidSubmissionDeadline, dates.evaluationTarget)
        : null;
    case "awardDecision":
      return dates.evaluationTarget && dates.awardTarget
        ? daysBetween(dates.evaluationTarget, dates.awardTarget)
        : null;
    case "contractSigning": {
      const anchor = dates.karensDate ?? dates.awardTarget;
      return anchor && dates.contractSigningTarget
        ? daysBetween(anchor, dates.contractSigningTarget)
        : null;
    }
    default:
      return null;
  }
};

// t() dynamic keys — langen extraction:
// t('days before bid deadline') t('days after announcement') t('days after bid deadline')
// t('days after evaluation') t('days after award') t('days after standstill')
// t('Day 0 · re-anchors at publish') t('Set exact date')
// t('Change') t('Announcement date')
const RELATIVE_LABEL: Partial<Record<MilestoneKey, string>> = {
  questionDeadline: "days before bid deadline",
  bidDeadline: "days after announcement",
  evaluationStart: "days after bid deadline",
  awardDecision: "days after evaluation",
  contractSigning: "days after standstill",
};

const END_OF_DAY = { hours: 23, minutes: 59 };

const formatDateLine1 = (iso: string): string =>
  new Date(iso).toLocaleDateString("nb-NO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Oslo",
  });

const formatTimeLine2 = (iso: string): string =>
  new Date(iso).toLocaleTimeString("nb-NO", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Oslo",
  });

const NodeDot = ({
  index,
  active,
  locked,
}: {
  index: number;
  active: boolean;
  locked?: boolean;
}) => (
  <Flex
    align="center"
    justify="center"
    boxSize="6"
    flexShrink={0}
    borderRadius="full"
    fontSize="xs"
    fontWeight="bold"
    bg={active ? "smPrimary" : "white"}
    color={active ? "white" : "smMuted"}
    borderWidth={locked ? "0" : "2px"}
    borderStyle={locked ? "none" : "solid"}
    borderColor={locked ? undefined : active ? "smPrimary" : "smBorder"}
  >
    {locked ? <FiLock size={11} /> : index + 1}
  </Flex>
);

const StackedDate = ({ iso }: { iso: string | null | undefined }) => {
  const { t } = useTranslation();
  if (!iso) {
    return (
      <Text fontSize="xs" color="smMuted" fontFamily="mono">
        {t("Not set")}
      </Text>
    );
  }
  return (
    <Box fontFamily="mono" lineHeight="1.3">
      <Text fontSize="sm" fontWeight="semibold" color="smText" whiteSpace="nowrap">
        {formatDateLine1(iso)}
      </Text>
      <Text fontSize="xs" color="smMuted" whiteSpace="nowrap">
        {formatTimeLine2(iso)}
      </Text>
    </Box>
  );
};

const STEPPER_DEBOUNCE_MS = 400;

const OffsetStepper = ({
  nodeKey,
  offset,
  eventId,
}: {
  nodeKey: MilestoneKey;
  offset: number | null;
  eventId: string;
}) => {
  const { updateField } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const planField = PLAN_FIELD[nodeKey];
  // Track the most recently written value so rapid clicks within the debounce window
  // accumulate (refs update synchronously, avoiding stale-closure capture of `offset`).
  // Reset when the server-derived `offset` catches up.
  const lastWrittenRef = useRef<number | null>(null);
  useEffect(() => {
    if (lastWrittenRef.current === offset) lastWrittenRef.current = null;
  }, [offset]);

  if (!planField) return null;

  const display = lastWrittenRef.current ?? offset ?? 0;

  // Read the ref directly in the handler so consecutive clicks in the same React batch
  // (no re-render between them) still see the latest written value.
  const write = (delta: number) => {
    const fresh = lastWrittenRef.current ?? offset ?? 0;
    const next = Math.max(0, fresh + delta);
    lastWrittenRef.current = next;
    const confirmPatch =
      nodeKey === "bidDeadline" ? { bidSubmissionDeadlineConfirmed: true } : {};
    updateField(
      { eventData: { ...confirmPatch, milestonePlan: { [planField]: next } } },
      { debounceMs: STEPPER_DEBOUNCE_MS },
    );
  };

  const handleMinus = () => write(-1);
  const handlePlus = () => write(+1);

  return (
    <Flex
      align="center"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="md"
      overflow="hidden"
      h="8"
      flexShrink={0}
    >
      <Box
        as="button"
        type="button"
        w="7"
        h="full"
        fontSize="md"
        color="smMuted"
        _hover={{ bg: "smPrimarySofter", color: "smPrimary" }}
        onClick={handleMinus}
        aria-label="Decrease offset"
      >
        –
      </Box>
      <Flex
        align="center"
        justify="center"
        minW="8"
        h="full"
        borderLeftWidth="1px"
        borderRightWidth="1px"
        borderColor="smBorder"
        px="1"
        fontFamily="mono"
        fontWeight="semibold"
        fontSize="sm"
      >
        {display}
      </Flex>
      <Box
        as="button"
        type="button"
        w="7"
        h="full"
        fontSize="md"
        color="smMuted"
        _hover={{ bg: "smPrimarySofter", color: "smPrimary" }}
        onClick={handlePlus}
        aria-label="Increase offset"
      >
        +
      </Box>
    </Flex>
  );
};

const CalendarOverride = ({
  nodeKey,
  iso,
  eventId,
  milestoneDates,
}: {
  nodeKey: MilestoneKey;
  iso: string | null | undefined;
  eventId: string;
  milestoneDates: SourcingEventV2Dto["milestoneDates"];
}) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleChange = (pickedIso?: string) => {
    if (!pickedIso) return;
    const patch = milestoneOffsetPatch(nodeKey, pickedIso, milestoneDates);
    if (patch) {
      updateField(patch);
      onClose();
    }
  };

  // Constrain the calendar so the buyer can't pick an order-violating date.
  // Each node's valid range is anchored to the milestone it logically depends on.
  const toDate = (iso: string | null | undefined) => (iso ? new Date(iso) : undefined);
  const minDate =
    nodeKey === "bidDeadline" ? toDate(milestoneDates.announcement)
    : nodeKey === "evaluationStart" ? toDate(milestoneDates.bidSubmissionDeadline)
    : nodeKey === "awardDecision" ? toDate(milestoneDates.evaluationTarget)
    : nodeKey === "contractSigning" ? toDate(milestoneDates.karensDate ?? milestoneDates.awardTarget)
    : undefined;
  // Question deadline is "before bid" — bid is the upper bound.
  const maxDate = nodeKey === "questionDeadline" ? toDate(milestoneDates.bidSubmissionDeadline) : undefined;

  return (
    <Popover isOpen={isOpen} onClose={onClose} isLazy placement="top">
      <PopoverTrigger>
        <IconButton
          aria-label={t("Set exact date") ?? "Set exact date"}
          icon={<FiCalendar size={13} />}
          size="xs"
          variant="ghost"
          color="smPrimary"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="smTealShadow"
          bg="smPrimarySofter"
          _hover={{ bg: "smTealShadow" }}
          onClick={onOpen}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="auto" bg="transparent" border="none" boxShadow="none" _focus={{ boxShadow: "none" }}>
          <PopoverBody p="0">
            <ThemedDateTimePicker
              ariaLabel={t("Set exact date") ?? "Set exact date"}
              date={iso}
              onChange={handleChange}
              defaultTime={END_OF_DAY}
              minDate={minDate}
              maxDate={maxDate}
              inline
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const ANNOUNCEMENT_FIELD_PATH = "eventData.milestonePlan.announcementDate";

// The announcement is the day-0 anchor, not an offset — so it gets a calendar override
// (mirroring CalendarOverride) instead of a stepper, plus a seed-on-mount: when no date
// is set yet, default it to today 23:59 so the rest of the plan has an anchor to compute from.
const AnnouncementCalendarOverride = ({
  iso,
  eventId,
}: {
  iso: string | null;
  eventId: string;
}) => {
  const { t } = useTranslation();
  const { updateField, fieldStatus } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(
    eventId,
  );
  const { isOpen, onOpen, onClose } = useDisclosure();

  const status = fieldStatus(ANNOUNCEMENT_FIELD_PATH);
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    if (iso !== null) return;
    if (status === "saving") return;
    prefilledRef.current = true;
    updateField({
      eventData: {
        milestonePlan: {
          announcementDate: moment()
            .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
            .toISOString(),
        },
      },
    });
  }, [iso, status, updateField]);

  const handleChange = (pickedIso?: string) => {
    updateField({ eventData: { milestonePlan: { announcementDate: pickedIso ?? null } } });
    if (pickedIso) onClose();
  };

  return (
    <Popover isOpen={isOpen} onClose={onClose} isLazy placement="top">
      <PopoverTrigger>
        <IconButton
          aria-label={t("Set exact date") ?? "Set exact date"}
          icon={<FiCalendar size={13} />}
          size="xs"
          variant="ghost"
          color="smPrimary"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="smTealShadow"
          bg="smPrimarySofter"
          _hover={{ bg: "smTealShadow" }}
          onClick={onOpen}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="auto" bg="transparent" border="none" boxShadow="none" _focus={{ boxShadow: "none" }}>
          <PopoverBody p="0">
            <ThemedDateTimePicker
              ariaLabel={t("Set exact date") ?? "Set exact date"}
              date={iso}
              onChange={handleChange}
              defaultTime={END_OF_DAY}
              inline
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

// Soft, informational only: the legal minimum tender period (minTenderDays) is not enforced
// here — the hard publish-block lives elsewhere. We reuse the existing BFF field-error
// translation so the message is consistent across the wizard.
const StatutoryBadge = ({ days }: { days: number }) => {
  const { t } = useTranslation();
  return (
    <Flex
      direction="column"
      align="center"
      gap="0"
      mt="1"
      px="3"
      py="1.5"
      borderRadius="md"
      bg="smWarningSoft"
      borderWidth="1px"
      borderColor="smWarningSoftBorder"
      fontSize="xs"
      fontWeight="bold"
      color="smWarning"
      textTransform="uppercase"
      letterSpacing="wider"
      lineHeight="1.3"
    >
      <Text>{t("Statutory")}</Text>
      <Text>{days} {t("days")}</Text>
    </Flex>
  );
};

const BidMinimumWarning = ({ minDays, minimumAt }: { minDays: number; minimumAt: string }) => {
  const { t } = useTranslation();
  return (
    <Text fontSize="xs" color="smError" mt="1">
      {t("zz_FieldErrorCode_TenderDeadlineBelowMinimum", { minDays, minimumAt })}
    </Text>
  );
};

const ConfirmBidDeadlineButton = ({ eventId }: { eventId: string }) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  return (
    <Button
      size="xs"
      colorScheme="teal"
      variant="solid"
      mt="1"
      onClick={() => updateField({ eventData: { bidSubmissionDeadlineConfirmed: true } })}
    >
      {t("Confirm bid deadline")}
    </Button>
  );
};

export const MilestoneTimeline = ({ event, mode, deadlineBadge }: MilestoneTimelineProps) => {
  const { t } = useTranslation();
  const { id: eventId, publicContext, milestoneDates, eventData } = event;
  const foaPart = publicContext.foaPart ?? null;
  const karensDays = autoKarensDays(foaPart);
  const minBidDays = minTenderDays(foaPart);
  const needsBidConfirm = eventData.bidSubmissionDeadlineConfirmed === false;

  // Fall back to the plan announcement when the derived value is null, else bid picks no-op (SM-676).
  const resolvedDates = {
    ...milestoneDates,
    announcement: milestoneDates.announcement ?? eventData.milestonePlan?.announcementDate ?? null,
  };

  const nodes = MILESTONE_NODES.filter(
    (node) => node.key !== "standstillExpiry" || karensDays != null,
  );

  return (
    <Box position="relative">
      {/* Connecting rail — at xl and up a horizontal line; below it a vertical rail.
          xl (1280px) is the threshold where all 7 nodes provably fit horizontally;
          below it they would clip, so the vertical rail takes over. */}
      <Box
        position="absolute"
        bg="smBorder"
        zIndex={0}
        top={{ base: "10px", xl: "11px" }}
        left={{ base: "11px", xl: "3%" }}
        right={{ base: "auto", xl: "3%" }}
        bottom={{ base: "10px", xl: "auto" }}
        height={{ base: "auto", xl: "2px" }}
        width={{ base: "2px", xl: "auto" }}
      />

      <Flex
        direction={{ base: "column", xl: "row" }}
        flexWrap="nowrap"
        justify={{ xl: "center" }}
        position="relative"
        zIndex={1}
        gap={{ base: 3, xl: 1 }}
      >
        {nodes.map((node, index) => {
          const isBid = node.key === "bidDeadline";
          const isAnnouncement = node.key === "announcement";
          const isStandstill = node.key === "standstillExpiry";
          const dateIso = resolvedDates[node.dateField];
          const offset = isStandstill ? null : deriveOffset(node.key, resolvedDates);
          const relativeLabel = RELATIVE_LABEL[node.key];
          const isEditable = mode === "edit" && node.editable && !isStandstill;

          return (
            <Flex
              key={node.key}
              flex={{ xl: "1 1 0" }}
              maxW={{ xl: "160px" }}
              direction={{ base: "row", xl: "column" }}
              align={{ base: "flex-start", xl: "center" }}
              textAlign={{ base: "left", xl: "center" }}
              gap={{ base: 3, xl: 2 }}
              px={{ xl: 1 }}
            >
              <NodeDot index={index} active={isBid} locked={isStandstill} />

              <Flex
                direction="column"
                align={{ base: "flex-start", xl: "center" }}
                gap="1"
                minW="0"
                flex="1"
              >
                {/* Label row */}
                <Box w="full" minH={{ xl: "2.5rem" }}>
                  <Flex
                    align="center"
                    gap="1.5"
                    justify={{ base: "flex-start", xl: "center" }}
                    wrap="wrap"
                  >
                    <Text fontSize="sm" fontWeight="semibold" color="smText" lineHeight="1.2">
                      {t(node.labelKey)}
                    </Text>
                    {isBid && deadlineBadge && (
                      <Box as="span" display="inline-flex" alignItems="center" lineHeight="1">
                        {deadlineBadge}
                      </Box>
                    )}
                  </Flex>
                </Box>

                {isEditable ? (
                  <Flex direction="column" align={{ base: "flex-start", xl: "center" }} gap="1">
                    {isAnnouncement ? (
                      <AnnouncementCalendarOverride
                        iso={eventData.milestonePlan?.announcementDate ?? null}
                        eventId={eventId}
                      />
                    ) : (
                      <CalendarOverride
                        nodeKey={node.key}
                        iso={dateIso}
                        eventId={eventId}
                        milestoneDates={resolvedDates}
                      />
                    )}
                    <StackedDate iso={dateIso} />
                  </Flex>
                ) : (
                  <StackedDate iso={dateIso} />
                )}

                {/* Controls — offset stepper (most nodes) or day-0 helper (announcement) */}
                {isEditable &&
                  (isAnnouncement ? (
                    <Text fontSize="xs" color="smMuted" textAlign={{ base: "left", xl: "center" }}>
                      {t("Day 0 · re-anchors at publish")}
                    </Text>
                  ) : (
                    <Flex
                      direction={{ base: "row", xl: "column" }}
                      align="center"
                      gap="1.5"
                      mt="1"
                    >
                      <OffsetStepper nodeKey={node.key} offset={offset} eventId={eventId} />
                      {relativeLabel && (
                        <Text
                          fontSize="xs"
                          color="smMuted"
                          textAlign={{ base: "left", xl: "center" }}
                        >
                          {t(relativeLabel)}
                        </Text>
                      )}
                    </Flex>
                  ))}

                {/* Soft bid-minimum warning — bid node only, edit mode, when the derived
                    offset is below the legal minimum tender period. Informational, not a block. */}
                {isEditable &&
                  isBid &&
                  minBidDays != null &&
                  offset != null &&
                  offset < minBidDays &&
                  resolvedDates.announcement != null && (
                    <BidMinimumWarning
                      minDays={minBidDays}
                      minimumAt={new Date(
                        new Date(resolvedDates.announcement).getTime() + minBidDays * 86_400_000,
                      ).toISOString()}
                    />
                  )}

                {isEditable && isBid && needsBidConfirm && (
                  <ConfirmBidDeadlineButton eventId={eventId} />
                )}

                {node.key === "standstillExpiry" && karensDays != null && (
                  <StatutoryBadge days={karensDays} />
                )}

                {/* Announcement day-0 label in summary / non-edit modes */}
                {isAnnouncement && !isEditable && (
                  <Text fontSize="xs" color="smMuted" mt="0.5">
                    {t("Day 0 · re-anchors at publish")}
                  </Text>
                )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
};
